import React, { useMemo } from 'react';
import { PhoneOutlined } from '@ant-design/icons';
import { Popover } from 'antd';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { IBook } from '../../../section/Common/types/types';
import { useActions } from '../../../context/Actions.context';
import { GAEventIdentifier, GAEventName } from '../../../section/Common/constants';
import { useUpdateTitle } from '../../../core/modules/book/hooks/useUpdateTitle.query';
import { CONTACTED, CONTACTED_AO3, CONTACTED_RR, SOURCE } from '../../../core/constants';
import Endpoints from '../../../core/constants/endpoints';
import { useServices } from '../../../core/services/useServices';
import showActionNotification from '../../../section/Common/utils/showActionNotification';

interface IProps {
  book: IBook;
  id: string;
  label?: string;
}

const createContactedObject = (source: string, book: IBook) => {
  if (source === SOURCE.bookstat) {
    return { amazonasin: book.ASIN };
  } else if (source === SOURCE.ao3)
  {
    return { work_id: book.work_id };
  }
  else {
    return { fiction_id: book['Fiction Id'] };
  }
};

const getContactedType = (source: string) => {
  if (source === SOURCE.bookstat) {
    return CONTACTED;
  } else if (source === SOURCE.ao3)
  {
    return CONTACTED_AO3;
  }
  else {
    return CONTACTED_RR;
  }
};

const ToContacted: React.FC<IProps> = ({ book, id, label = 'Title' }) => {
  const { source, setActionLoading } = useActions();
  const { book: bookService } = useServices();
  const queryClient = useQueryClient();
  const history = useHistory();

  const contacted = useMemo(() => createContactedObject(source?.['source'], book), [source, book]);
  const contactedType = useMemo(() => getContactedType(source?.['source']), [source]);

  const { mutateAsync: mutateContacted } = useUpdateTitle(contactedType);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    globalThis.gtag('event', GAEventName.click, {
      identifier: GAEventIdentifier.titletoContacted,
      value: book.ASIN,
      book_name: book.Title
    });

    setActionLoading(true);

    const capturedSource = source?.['source'];
    const capturedParams = { ...contacted };

    const promise = mutateContacted({ params: contacted, body: {} });

    promise.then(res => {
      if (res?.status === 200) {
        showActionNotification({
          type: 'success',
          message: 'The Title was successfully sent to the Contacted tab.',
          destination: {
            name: 'Contacted',
            onGoTo: () => history.push(`${capturedSource}/contacted`)
          },
          onUndo: async () => {
            await bookService.updateTitle(
              {
                source: capturedSource,
                sourceTab: 'contacted',
                endpoint: Endpoints[id],
                action: id
              },
              capturedParams
            );
          },
          queryClient,
          id
        });
      } else {
        showActionNotification({
          type: 'error',
          message: 'The Title has not been sent to the Contacted tab.'
        });
      }
    }).catch(() => {
      showActionNotification({
        type: 'error',
        message: 'The Title has not been sent to the Contacted tab.'
      });
    });

    promise.then(() => queryClient.refetchQueries(`get-row-count${id}`));
    promise.then(() => queryClient.refetchQueries(`get-${id}`));
    promise.finally(() => setActionLoading(false));
  };

  return (
    <Popover
      placement='topLeft'
      content={<div className='popover-content'>{`Send ${label} to Contacted`}</div>}
      trigger='hover'
    >
      <PhoneOutlined
        data-testid='action-contacted-item'
        onClick={handleClick}
        className='actions-icons actions-icons-contact'
      />
    </Popover>
  );
};

export default React.memo(ToContacted);
